import { ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { PortalContext } from '../../../context/portal-context';
import { useTrackingManager } from '../../../tracking/use-tracking-manager';
import { ErrorType } from '@esportal/portal-service';
import { formatOwnership } from '../../../tracking/tracking-helper';
import { AlertVehicleManagerLayer } from '../layer/alert-vehicle-manager-layer';

export const useAlertVehicleManagerLayer = () => {
	const portalContext = useContext(PortalContext);
	const userVehicles = portalContext.portalService.getPersonVehicles().getValue();
	const trackingManager = useTrackingManager();
	const errorCountRef = React.useRef<number>(0);
	const errorCountsRef = React.useRef<Partial<Record<ErrorType, number>>>({});
	const isLayerOpenRef = React.useRef(false);
	const [isLayerOpen, setIsLayerOpen] = useState(false);
	const [errorType, setErrorType] = useState(ErrorType.NonDefined);

	const handleLayerOpen = useCallback(
		(type: ErrorType = ErrorType.NonDefined) => {
			if (isLayerOpenRef.current) return;

			isLayerOpenRef.current = true;
			const errorCount = (errorCountsRef.current[type] ?? 0) + 1;
			errorCountsRef.current[type] = errorCount;
			errorCountRef.current = errorCount;
			setErrorType(type);
			setIsLayerOpen(true);

			trackingManager.trackClubVWErrorMessageLoad(
				{ message: errorCount > 1 ? 'Uy, ha habido un error' : '¡Ops, ha habido un error!' },
				{ contentId: `Error: ${type}` },
			);
		},
		[trackingManager],
	);

	const handleLayerCloseButtonClick = useCallback(() => {
		isLayerOpenRef.current = false;
		setIsLayerOpen(false);

		trackingManager.trackClubVWLayerCloseClick('Error layer', {
			contentId: `Error layer: ${errorType}`,
		});
	}, [trackingManager, errorType]);

	const handleLayerCloseVolverClick = useCallback(() => {
		isLayerOpenRef.current = false;
		setIsLayerOpen(false);

		trackingManager.trackClubVWButtonClick(
			'Volver',
			{
				contentId: `Close Error layer: ${errorType}`,
			},
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
	}, [trackingManager, errorType, userVehicles]);

	useEffect(() => {
		if (!isLayerOpen) return;
		const layer = portalContext.layerManager.openAlert(
			() => (
				<AlertVehicleManagerLayer
					onButtonClickClose={handleLayerCloseButtonClick}
					onVolverClose={handleLayerCloseVolverClick}
					errorCountRef={errorCountRef}
				/>
			),
			{},
			{
				shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
			},
		);

		return () => {
			layer.close();
		};
	}, [isLayerOpen, portalContext, handleLayerCloseButtonClick, handleLayerCloseVolverClick]);

	return {
		handleLayerOpen,
	};
};
